/**
 * Creative tag downloads — the files handed over for trafficking in a
 * third-party system. Ported from the old UI's creative tag component.
 */
import writeXlsxFile, { type Row } from 'write-excel-file/browser'
import { createZip } from '@/utils/zip'
import { saveBlob } from '@/utils/download'
import {
  clickTracker,
  creativeTag,
  generateTargetId,
  impressionTracker,
  zippedHtml,
  type TagContext,
  type TagCreative,
  type TagFormat,
} from '@/services/creatives/creativeTags'

export type DownloadFormat = 'ZIPPED_TXT' | 'ZIPPED_HTML' | 'TXT' | 'XLS'

/** The surrounding objects the trafficking spreadsheets describe. */
export interface TagDownloadContext {
  lineItem: { id: string; name: string; startDate?: string; endDate?: string }
  order: { id: string; name: string }
  advertiser: { id: string; name: string; url?: string }
}

function kebabCase(value: string): string {
  return (value || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/ /g, '-')
}

/** `2026-09-11 14:30:00`, the format the trafficking sheets expect. */
function sheetDateTime(value?: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

const HEADER = {
  fontWeight: 'bold',
  align: 'center',
  color: '#ffffff',
  backgroundColor: '#0d5d9e',
  width: 30,
  height: 25,
  alignVertical: 'center',
} as const

const header = (value: string, span?: number) => ({ ...HEADER, value, ...(span ? { span } : {}) })
const cell = (value: string) => ({ value, alignVertical: 'top' as const })

/**
 * Builds and saves the tag files for `creatives`.
 *
 * `ZIPPED_HTML` ignores `format` — it always serves through the standard
 * adn.js loader, as the old UI does.
 */
export async function downloadCreativeTags(
  downloadFormat: DownloadFormat,
  format: TagFormat,
  ctx: TagContext,
  creatives: TagCreative[],
  info: TagDownloadContext
): Promise<void> {
  const fileBase = kebabCase(info.lineItem.name || 'download')
  const targetId = generateTargetId()
  const tagFor = (creative: TagCreative, index: number) =>
    creativeTag(format, ctx, creative, `${targetId}-${index + 1}`)

  if (downloadFormat === 'ZIPPED_HTML') {
    const zip = createZip(
      creatives.map((c) => ({
        name: `${kebabCase(c.name)}.html`,
        content: zippedHtml(ctx, c, generateTargetId()),
      }))
    )
    saveBlob(zip, `${fileBase}.zip`)
    return
  }

  if (downloadFormat === 'ZIPPED_TXT') {
    const zip = createZip(
      creatives.map((c, i) => ({ name: `${kebabCase(c.name)}.txt`, content: tagFor(c, i) }))
    )
    saveBlob(zip, `${fileBase}.zip`)
    return
  }

  if (downloadFormat === 'TXT') {
    const text = creatives.map((c, i) => tagFor(c, i)).join('\n\n\n') + '\n\n\n'
    saveBlob(new Blob([text], { type: 'text/plain' }), `${fileBase}.txt`)
    return
  }

  if (format === 'GOOGLE_CAMPAIGN_MANAGER') {
    await writeGoogleCampaignManagerXls(fileBase, tagFor, creatives, info)
  } else if (format === 'ADFORM') {
    await writeAdFormXls(fileBase, targetId, tagFor, creatives, ctx, info)
  }
}

type TagBuilder = (creative: TagCreative, index: number) => string

async function writeGoogleCampaignManagerXls(
  fileBase: string,
  tagFor: TagBuilder,
  creatives: TagCreative[],
  info: TagDownloadContext
): Promise<void> {
  const rows: Row[] = [
    [header('Contract Information', 2)],
    [{ value: 'Advertiser ID' }, { value: info.advertiser.id }],
    [{ value: 'Advertiser Name' }, { value: info.advertiser.name }],
    [{ value: 'Campaign ID' }, { value: info.order.id }],
    [{ value: 'Campaign Name' }, { value: info.order.name }],
    [{ value: '' }],
    [
      'Advertiser ID', 'Advertiser Name', 'Campaign ID', 'Campaign Name', 'Placement ID',
      'Placement External ID', 'Site', 'Placement Name', 'Placement Compatibility', 'Dimensions',
      'Start Date', 'End Date', 'Standard Tag', 'Iframes/JavaScript Tag', 'Internal Redirect Tag',
      'JavaScript Tag',
    ].map((h) => header(h)),
    ...creatives.map((c, i) => {
      const code = tagFor(c, i)
      return [
        cell(info.advertiser.id),
        cell(info.advertiser.name),
        cell(info.order.id),
        cell(info.order.name),
        cell(info.lineItem.id),
        cell(''),
        cell(''),
        cell(info.lineItem.name),
        cell('Display'),
        cell(`${c.width}x${c.height}`),
        cell(sheetDateTime(info.lineItem.startDate)),
        cell(sheetDateTime(info.lineItem.endDate)),
        cell(''),
        cell(code),
        cell(''),
        cell(code),
      ]
    }),
  ]

  const blob = await writeXlsxFile(rows, {
    columns: [
      { width: 30 }, { width: 30 }, { width: 30 }, { width: 30 }, { width: 30 }, { width: 5 },
      { width: 30 }, { width: 30 }, { width: 30 }, { width: 30 }, { width: 30 }, { width: 30 },
      { width: 30 }, { width: 30 }, { width: 30 }, { width: 30 },
    ],
  }).toBlob()
  saveBlob(blob, `${fileBase}.xlsx`)
}

async function writeAdFormXls(
  fileBase: string,
  targetId: string,
  tagFor: TagBuilder,
  creatives: TagCreative[],
  ctx: TagContext,
  info: TagDownloadContext
): Promise<void> {
  const blank = Array.from({ length: 4 }, () => null)
  const rows: Row[] = [
    [header('Summary', 2)],
    [{ value: 'Export date' }, { value: sheetDateTime(new Date().toISOString()).slice(0, 10) }],
    [{ value: 'Advertiser name' }, { value: info.advertiser.name }],
    [{ value: 'Campaign name' }, { value: info.order.name }],
    [{ value: 'Campaign ID' }, { value: info.order.id }],
    [{ value: '' }],
    [
      header('Line item details', 5), ...blank,
      { value: '' },
      header('Tag details', 17), ...Array.from({ length: 16 }, () => null),
    ],
    [
      ...['Media', 'Line item name', 'Line item ID', 'Start date', 'End date'].map((h) => header(h)),
      { value: '' },
      ...[
        'Type', 'Tag ID', 'Tag name', 'Ad type', 'Dimensions', 'Script', 'Additional information',
        'Target URL (click tracker)', '1x1 pixel counter (impression tracker)', 'Format',
        'Destination URL', 'Append URL', 'Replace URL', 'Impression tracking pixel',
        'Click tracking pixel', 'Ad name', 'Preview',
      ].map((h) => header(h)),
    ],
    ...creatives.map((c, i) => [
      cell(info.advertiser.url ?? ''),
      cell(info.lineItem.name),
      cell(info.lineItem.id),
      cell(sheetDateTime(info.lineItem.startDate)),
      cell(sheetDateTime(info.lineItem.endDate)),
      cell(''),
      cell('Direct'),
      cell(`${targetId}-${i + 1}`),
      cell(`${kebabCase(`${c.name} ${c.width}`)}x${c.height}`),
      cell(''),
      cell(`${c.width}x${c.height}`),
      cell(tagFor(c, i)),
      cell(''),
      cell(clickTracker(ctx, c)),
      cell(impressionTracker(ctx, c)),
      cell('javascriptDefault'),
      cell(''),
      cell(''),
      cell(''),
      cell(''),
      cell(''),
      cell(c.name),
      cell(''),
    ]),
  ]

  const blob = await writeXlsxFile(rows, {
    columns: [
      { width: 30 }, { width: 30 }, { width: 30 }, { width: 30 }, { width: 30 }, { width: 5 },
      ...Array.from({ length: 17 }, () => ({ width: 30 })),
    ],
  }).toBlob()
  saveBlob(blob, `${fileBase}.xlsx`)
}
